import React, {useEffect, useState, useCallback, useRef} from 'react';
import "../HintCards/StyleHintCard.scss"
import {useLocation, useNavigate} from "react-router-dom";
import {GET_ROLE, REQUEST} from "../../../utils/js/Const";
import {FetchElements, GetCookie, updateRefreshToken} from "../../../utils/js/request";
import ButtonBack from "../../../components/Buttons/buttonBack/ButtonBack";
import ButtonHint from "../../../components/Buttons/buttonHint/ButtonHint";
import CustomAlert from "../../../components/CustomAlert/CustomAlert";

const HintSeries = () => {
    let navigate = useNavigate();
    let location = useLocation();
    let {state} = location;
    let role = GET_ROLE();

    const guid = window.location.pathname.split('/').pop();
    localStorage.setItem("guid", guid);
    let _guid = localStorage.getItem("guid");

    const taskId = (state && state.task_id !== null && state.task_id !== undefined)
        ? state.task_id
        : guid || _guid;

    const [seriesData, setSeriesData] = useState([]);
    const [bufferData, setBufferData] = useState([]);
    const [resultData, setResultData] = useState();
    const [accessToken, setAccessToken] = useState(GetCookie("access_token"));
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [changes, setChanges] = useState({});

    // Счётчик для уникальных id buffer-строк (защита от Date.now() коллизий)
    const bufferIdRef = useRef(0);

    const [alertState, setAlertState] = useState({
        isOpen: false,
        title: '',
        message: '',
        statusCod: '',
        isConfirmMode: false,
        confirmCallback: null,
        confirmText: 'Подтвердить',
        cancelText: 'Отмена'
    });

    const handleOpenAlert = (title, message, statusCod, options = {}) => {
        setAlertState({
            isOpen: true,
            title,
            message,
            statusCod,
            isConfirmMode: options.isConfirmMode || false,
            confirmCallback: options.confirmCallback || null,
            confirmText: options.confirmText || 'Подтвердить',
            cancelText: options.cancelText || 'Отмена'
        });
    };

    const handleCloseAlert = () => {
        if (alertState.confirmCallback) {
            alertState.confirmCallback(false);
        }
        setAlertState(prev => ({ ...prev, isOpen: false }));
    };

    const handleConfirmAlert = () => {
        if (alertState.confirmCallback) {
            alertState.confirmCallback(true);
        }
        setAlertState(prev => ({ ...prev, isOpen: false }));
    };

    const checkUnsavedChanges = useCallback(() => {
        return Object.keys(changes).length > 0 || bufferData.length > 0;
    }, [changes, bufferData]);

    const showExitConfirmation = () => {
        return new Promise((resolve) => {
            handleOpenAlert(
                "Подтверждение выхода",
                "Вы действительно хотите выйти назад? Если вы выйдете сейчас, изменения не будут сохранены",
                "confirm",
                {
                    isConfirmMode: true,
                    confirmCallback: resolve,
                    confirmText: "Да, я хочу выйти",
                    cancelText: "Нет, я не хочу выходить"
                }
            );
        });
    };

    const showReloadConfirmation = () => {
        return new Promise((resolve) => {
            handleOpenAlert(
                "Подтверждение обновления",
                "У вас есть несохраненные изменения. Вы уверены, что хотите обновить страницу? Все несохраненные данные будут потеряны.",
                "confirm",
                {
                    isConfirmMode: true,
                    confirmCallback: resolve,
                    confirmText: "Да, обновить",
                    cancelText: "Отмена"
                }
            );
        });
    };

    const showWriteOffConfirmation = (item, quantityToWriteOff, currentNumber, totalCount) => {
        return new Promise((resolve) => {
            const message =
                "Имеется просроченный товар:\n\n" +
                `Срок годности: ${formatDateToDDMMYYYY(item.series_date)}\n` +
                `Остаток по факту: ${item.fact_stock}шт.\n` +
                `Кол-во к уценке: ${item.kx_ucenke}шт.\n\n` +
                `Необходимо списать ${quantityToWriteOff}шт.\n\n` +
                `Строк ${currentNumber} из ${totalCount}`;
            handleOpenAlert(
                "Подтверждение списания",
                message,
                "confirm",
                {
                    isConfirmMode: true,
                    confirmCallback: resolve,
                    confirmText: "Подтвердить",
                    cancelText: "Отмена"
                }
            );
        });
    };

    async function getSeriesData() {
        FetchElements(
            `${REQUEST}/api/v1/tasks/${taskId}/series`,
            'GET',
            (data) => {
                if (Array.isArray(data)) {
                    setSeriesData(data);
                } else {
                    setSeriesData([]);
                    console.warn("API вернул не массив, устанавливаем пустой массив");
                }
            },
            navigate,
            2,
            () => {},
            (title, message, statusCod) => handleOpenAlert(title, message, statusCod),
            0,
            null,
            [404],
        );
    }

    async function getTask() {
        if (resultData === undefined) {
            FetchElements(
                `${REQUEST}/api/v1/tasks/${taskId}`,
                'GET',
                (data) => setResultData(data),
                navigate,
                2,
                () => {},
                (title, message, statusCod) => handleOpenAlert(title, message, statusCod)
            );
        }
    }

    const loadData = () => {
        getSeriesData();
        getTask();
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!accessToken) {
                const newToken = await updateRefreshToken(setAccessToken, null, () => {
                    navigate("/")
                });
                if (newToken) {
                    loadData();
                }
            } else {
                loadData();
            }
        };
        fetchData();
    }, [accessToken]);

    useEffect(() => {
        setHasUnsavedChanges(checkUnsavedChanges());
    }, [changes, bufferData, checkUnsavedChanges]);

    const formatDateToDDMMYYYY = (dateString) => {
        if (!dateString) return "";
        if (typeof dateString === 'string' && dateString.includes('.')) {
            return dateString;
        }
        if (typeof dateString === 'string' && dateString.includes('-')) {
            const [year, month, day] = dateString.split('-');
            return `${day}.${month}.${year}`;
        }
        if (dateString instanceof Date) {
            const day = String(dateString.getDate()).padStart(2, '0');
            const month = String(dateString.getMonth() + 1).padStart(2, '0');
            const year = dateString.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    const formatDateToYYYYMMDD = (dateString) => {
        if (!dateString) return "";
        if (typeof dateString === 'string' && dateString.includes('.')) {
            const [day, month, year] = dateString.split('.');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateString;
    };

    const isDateExpired = (dateString) => {
        if (!dateString) return false;
        try {
            const seriesDate = new Date(formatDateToYYYYMMDD(dateString));
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            return seriesDate < currentDate;
        } catch (error) {
            console.error('Ошибка при обработке даты:', error);
            return false;
        }
    };

    const getFieldValue = useCallback((item, field, isBuffer, index) => {
        const key = isBuffer ? `buffer_${item.id}` : `series_${item.guid}`;
        const change = changes[key];

        if (change && change[field] !== undefined) {
            return change[field];
        }

        if (!isBuffer) {
            if (field === 'series_date') {
                return item[field] || '';
            }
            // Явно приводим к строке, чтобы 0 отображался как '0', а не пустота
            return (item[field] !== undefined && item[field] !== null) ? String(item[field]) : '';
        }

        return '';
    }, [changes]);

    const isFieldFilled = useCallback((item, field, isBuffer) => {
        const key = isBuffer ? `buffer_${item.id}` : `series_${item.guid}`;
        const change = changes[key];

        // 1. Пользователь явно трогал поле и ввёл что-то (даже '0' — это заполнено)
        if (change && change[`${field}_touched`] === true) {
            return change[field] !== undefined && change[field] !== '';
        }

        // 2. Не трогал — смотрим на данные с бэка
        const raw = item[field];
        if (field === 'series_date') {
            return raw !== undefined && raw !== null && raw !== '';
        }
        // Для числовых: 0 с бэка — валидное заполненное значение
        return raw !== undefined && raw !== null;
    }, [changes]);

    const handleValueChange = useCallback((item, field, value, isBuffer, index) => {
        const key = isBuffer ? `buffer_${item.id}` : `series_${item.guid}`;

        // Очищаем от всех нецифровых символов, кроме точки
        let cleaned = value.replace(/[^\d.]/g, '');

        // Убираем лишние точки
        const parts = cleaned.split('.');
        if (parts.length > 2) {
            cleaned = parts[0] + '.' + parts.slice(1).join('');
        }

        // Одинокая точка — считаем пустым
        if (cleaned === '.') {
            cleaned = '';
        }

        // Пустую строку НЕ превращаем в '0' — оставляем пустой,
        // чтобы пользователь видел, что поле не заполнено, и placeholder работал.
        setChanges(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: cleaned,
                [`${field}_touched`]: true,
            }
        }));
        setHasUnsavedChanges(true);
    }, [setChanges]);

    const handleInputFocus = useCallback((e) => {
        e.target.select();
    }, []);

    function addMore() {
        const hasEmptyBuffer = bufferData.some(item => {
            const change = changes[`buffer_${item.id}`] || {};
            return !(
                change.series_date_touched ||
                change.fact_stock_touched ||
                change.kx_ucenke_touched ||
                change.kx_spisaniyu_touched
            );
        });

        if (hasEmptyBuffer) {
            handleOpenAlert("Внимание", "Заполните или удалите существующую пустую строку перед добавлением новой", 400);
            return;
        }

        const newBufferItem = {
            id: `buf_${Date.now()}_${++bufferIdRef.current}`,
            series_date: "",
            plane_stock: 0,
            fact_stock: "",
            kx_ucenke: "",
            kx_spisaniyu: "",
            isBuffer: true
        };

        setBufferData(prev => [newBufferItem, ...prev]);
        setHasUnsavedChanges(true);
    }

    const removeBufferItem = (id) => {
        setBufferData(prev => prev.filter(item => item.id !== id));
        setChanges(prev => {
            const newChanges = {...prev};
            delete newChanges[`buffer_${id}`];
            return newChanges;
        });
        setHasUnsavedChanges(checkUnsavedChanges());
    };

    const createNewSeries = (seriesData) => {
        const formattedData = {
            series_date: formatDateToDDMMYYYY(seriesData.series_date),
            plane_stock: Number(seriesData.plane_stock) || 0,
            fact_stock: Number(seriesData.fact_stock) || 0,
            kx_ucenke: Number(seriesData.kx_ucenke) || 0,
            kx_spisaniyu: Number(seriesData.kx_spisaniyu) || 0
        };

        return new Promise((resolve, reject) => {
            FetchElements(
                `${REQUEST}/api/v1/tasks/${taskId}/series`,
                'POST',
                (data) => resolve(data),
                navigate,
                2,
                () => {},
                (title, message, statusCod) => handleOpenAlert(title, message, statusCod),
                0,
                formattedData
            );
        });
    };

    const updateSeries = (updateDataArray) => {
        return new Promise((resolve, reject) => {
            FetchElements(
                `${REQUEST}/api/v1/tasks/${taskId}/series`,
                'PATCH',
                (data) => resolve(data),
                navigate,
                2,
                () => {},
                (title, message, statusCod) => {
                    handleOpenAlert(title, message, statusCod);
                    reject(new Error(message));
                },
                0,
                updateDataArray
            );
        });
    };

    const validateRowData = (rowData, isBuffer, isAllowUcen, filledFlags) => {
        // 1. Валидация для НОВЫХ (buffer) строк
        if (isBuffer) {
            const factStock = Number(rowData.fact_stock) || 0;
            let kxUcenke = Number(rowData.kx_ucenke) || 0;
            const kxSpisaniyu = Number(rowData.kx_spisaniyu) || 0;
            const seriesDate = rowData.series_date;

            if (!isAllowUcen) {
                kxUcenke = 0;
            }

            // Если дата пустая, и все числа 0 (или пустые) -> строка полностью пустая
            const isEmptyRow = !seriesDate && factStock === 0 && kxUcenke === 0 && kxSpisaniyu === 0;
            if (isEmptyRow) {
                return { isValid: false, error: "Заполните или удалите строку" };
            }

            // Если дата есть, но все числа 0 (или пустые) -> не заполнены числовые
            const hasDateButNoValues = seriesDate && factStock === 0 && kxUcenke === 0 && kxSpisaniyu === 0;
            if (hasDateButNoValues) {
                return { isValid: false, error: "Заполните числовые поля или удалите строку" };
            }

            // Если числа есть (хоть один > 0), но даты нет -> не заполнена дата
            const hasValuesButNoDate = !seriesDate && (factStock > 0 || kxUcenke > 0 || kxSpisaniyu > 0);
            if (hasValuesButNoDate) {
                return { isValid: false, error: "Заполните дату срока годности" };
            }

            // Проверка уценки/списания
            if (kxUcenke + kxSpisaniyu > factStock) {
                return {
                    isValid: false,
                    error: `Сумма уценки (${kxUcenke}) и списания (${kxSpisaniyu}) не может быть больше остатка по факту (${factStock})`
                };
            }

            return { isValid: true };
        }

        // 2. Валидация для СУЩЕСТВУЮЩИХ строк
        const factStock = Number(rowData.fact_stock) || 0;
        let kxUcenke = Number(rowData.kx_ucenke) || 0;
        const kxSpisaniyu = Number(rowData.kx_spisaniyu) || 0;

        if (!isAllowUcen) {
            kxUcenke = 0;
        }

        // Для существующих строк поля считаются валидными, так как они пришли с бэкенда
        // (даже если там 0). Но проверяем на отрицательные значения и логику
        if (factStock < 0 || kxUcenke < 0 || kxSpisaniyu < 0) {
            return { isValid: false, error: "Значения не могут быть отрицательными" };
        }

        if (kxUcenke + kxSpisaniyu > factStock) {
            return {
                isValid: false,
                error: `Сумма уценки (${kxUcenke}) и списания (${kxSpisaniyu}) не может быть больше остатка по факту (${factStock})`
            };
        }

        return { isValid: true };
    };

    async function accept() {
        try {
            const validationErrors = [];

            // Валидация buffer-строк
            for (const bufferItem of bufferData) {
                const change = changes[`buffer_${bufferItem.id}`] || {};
                const rowData = {
                    series_date: change.series_date ?? bufferItem.series_date ?? '',
                    fact_stock: change.fact_stock ?? bufferItem.fact_stock ?? '',
                    kx_ucenke: change.kx_ucenke ?? bufferItem.kx_ucenke ?? '',
                    kx_spisaniyu: change.kx_spisaniyu ?? bufferItem.kx_spisaniyu ?? '',
                };

                const filledFlags = {
                    dateFilled: isFieldFilled(bufferItem, 'series_date', true),
                    factFilled: isFieldFilled(bufferItem, 'fact_stock', true),
                    ucenFilled: isFieldFilled(bufferItem, 'kx_ucenke', true),
                    spisFilled: isFieldFilled(bufferItem, 'kx_spisaniyu', true),
                };

                const validation = validateRowData(rowData, true, resultData?.is_allow_ucen, filledFlags);
                if (!validation.isValid) {
                    validationErrors.push(validation.error);
                }
            }

            // Валидация существующих строк
            for (const item of seriesData) {
                const change = changes[`series_${item.guid}`] || {};
                const rowData = {
                    series_date: change.series_date ?? item.series_date ?? '',
                    fact_stock: change.fact_stock ?? item.fact_stock ?? '',
                    kx_ucenke: change.kx_ucenke ?? item.kx_ucenke ?? '',
                    kx_spisaniyu: change.kx_spisaniyu ?? item.kx_spisaniyu ?? '',
                };

                const filledFlags = {
                    dateFilled: isFieldFilled(item, 'series_date', false),
                    factFilled: isFieldFilled(item, 'fact_stock', false),
                    ucenFilled: isFieldFilled(item, 'kx_ucenke', false),
                    spisFilled: isFieldFilled(item, 'kx_spisaniyu', false),
                };

                const validation = validateRowData(rowData, false, resultData?.is_allow_ucen, filledFlags);
                if (!validation.isValid) {
                    validationErrors.push(`Ошибка в строке с датой ${formatDateToDDMMYYYY(item.series_date)}: ${validation.error}`);
                }
            }

            if (validationErrors.length > 0) {
                handleOpenAlert("Ошибка валидации", validationErrors.join('\n\n'), 400);
                return;
            }

            // Проверка списания просроченных товаров (только buffer-строки)
            const itemsForWriteOffCheck = [];

            for (const bufferItem of bufferData) {
                const change = changes[`buffer_${bufferItem.id}`] || {};
                const factStock = Number(change.fact_stock ?? bufferItem.fact_stock) || 0;
                const kxUcenke = Number(change.kx_ucenke ?? bufferItem.kx_ucenke) || 0;
                const seriesDate = change.series_date || bufferItem.series_date;

                if (isDateExpired(seriesDate) && factStock > 0) {
                    const remainingStock = factStock - kxUcenke;
                    if (remainingStock > 0) {
                        itemsForWriteOffCheck.push({
                            id: bufferItem.id,
                            series_date: seriesDate,
                            fact_stock: factStock,
                            kx_ucenke: kxUcenke,
                            remainingStock: remainingStock,
                            isBuffer: true
                        });
                    }
                }
            }

            const confirmedItems = [];
            const rejectedItems = [];
            let updatedChanges = {...changes};

            for (let i = 0; i < itemsForWriteOffCheck.length; i++) {
                const item = itemsForWriteOffCheck[i];
                const confirmed = await showWriteOffConfirmation(item, item.remainingStock, i + 1, itemsForWriteOffCheck.length);

                if (confirmed) {
                    confirmedItems.push(item);
                    updatedChanges = {
                        ...updatedChanges,
                        [`buffer_${item.id}`]: {
                            ...updatedChanges[`buffer_${item.id}`],
                            kx_spisaniyu: item.remainingStock,
                            kx_spisaniyu_touched: true,
                        }
                    };
                } else {
                    rejectedItems.push(item);
                    updatedChanges = {
                        ...updatedChanges,
                        [`buffer_${item.id}`]: {
                            ...updatedChanges[`buffer_${item.id}`],
                            kx_spisaniyu: 0,
                            kx_spisaniyu_touched: true,
                        }
                    };
                }
            }

            setChanges(updatedChanges);
            await saveDataToServer(updatedChanges, confirmedItems, rejectedItems);

        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            handleOpenAlert("Ошибка", "Произошла ошибка при сохранении", 500);
        }
    }

    async function saveDataToServer(currentChanges, confirmedItems, rejectedItems) {
        const operations = [];
        const updateDataArray = [];

        // Сохраняем новые строки (buffer)
        for (const bufferItem of bufferData) {
            const change = currentChanges[`buffer_${bufferItem.id}`] || {};

            const hasAnyTouch =
                change.series_date_touched ||
                change.fact_stock_touched ||
                change.kx_ucenke_touched ||
                change.kx_spisaniyu_touched;

            if (!hasAnyTouch) continue;

            const newSeriesData = {
                series_date: change.series_date || bufferItem.series_date || '',
                plane_stock: Number(change.plane_stock ?? bufferItem.plane_stock) || 0,
                fact_stock: Number(change.fact_stock ?? bufferItem.fact_stock) || 0,
                kx_ucenke: Number(change.kx_ucenke ?? bufferItem.kx_ucenke) || 0,
                kx_spisaniyu: Number(change.kx_spisaniyu ?? bufferItem.kx_spisaniyu) || 0
            };

            operations.push(createNewSeries(newSeriesData));
        }

        // Обновляем существующие строки
        for (const item of seriesData) {
            const change = currentChanges[`series_${item.guid}`] || {};

            const newFactStock = Number(change.fact_stock ?? item.fact_stock) || 0;
            const newKxUcenke = Number(change.kx_ucenke ?? item.kx_ucenke) || 0;
            const newKxSpisaniyu = Number(change.kx_spisaniyu ?? item.kx_spisaniyu) || 0;

            const originalFactStock = Number(item.fact_stock) || 0;
            const originalKxUcenke = Number(item.kx_ucenke) || 0;
            const originalKxSpisaniyu = Number(item.kx_spisaniyu) || 0;

            const hasFactStockChanged =
                newFactStock !== originalFactStock || change.fact_stock_touched === true;
            const hasKxUcenkeChanged =
                newKxUcenke !== originalKxUcenke || change.kx_ucenke_touched === true;
            const hasKxSpisaniyuChanged =
                newKxSpisaniyu !== originalKxSpisaniyu || change.kx_spisaniyu_touched === true;

            // Если изменений нет, НО это строка, которую надо сохранить принудительно
            // (например, если пользователь нажал "Применить"), мы всё равно её отправим,
            // чтобы гарантировать сохранение нулей.
            // В вашем случае — отправляем все строки, у которых есть дата (series_date).
            const shouldForceUpdate = !!item.guid && !!item.series_date;

            if (!hasFactStockChanged && !hasKxUcenkeChanged && !hasKxSpisaniyuChanged && !shouldForceUpdate) {
                continue;
            }

            if (resultData?.is_allow_ucen === false && hasKxUcenkeChanged && newKxUcenke !== 0) {
                handleOpenAlert("Ошибка", "Уценка запрещена для данной номенклатуры", 400);
                throw new Error("Уценка запрещена");
            }

            const finalKxUcenke = resultData?.is_allow_ucen === false ? 0 : newKxUcenke;

            const updateData = {
                guid: item.guid,
                series_date: formatDateToDDMMYYYY(item.series_date),
                plane_stock: Number(item.plane_stock) || 0,
                fact_stock: newFactStock,
                kx_ucenke: finalKxUcenke,
                kx_spisaniyu: newKxSpisaniyu
            };

            updateDataArray.push(updateData);
        }

        if (operations.length === 0 && updateDataArray.length === 0) {
            handleOpenAlert("Внимание", "Нет изменений для сохранения", 400);
            return;
        }

        try {
            if (updateDataArray.length > 0) {
                const patchResponseData = await updateSeries(updateDataArray);
                if (patchResponseData && Array.isArray(patchResponseData)) {
                    setSeriesData(patchResponseData);
                }
            }

            if (operations.length > 0) {
                await Promise.all(operations);
                await getSeriesData();
            }

            setBufferData([]);
            setChanges({});

            let successMessage = "Данные успешно сохранены";

            if (confirmedItems.length > 0 || rejectedItems.length > 0) {
                successMessage += `\n\nСписание просроченных товаров:`;
                if (confirmedItems.length > 0) {
                    successMessage += `\n Подтверждено: ${confirmedItems.length} шт.`;
                }
                if (rejectedItems.length > 0) {
                    successMessage += `\n Отклонено: ${rejectedItems.length} шт.`;
                }
            }

            handleOpenAlert("Успех", successMessage, 200);
            setTimeout(() => navigate(-1), 400);
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            handleOpenAlert("Ошибка", "Произошла ошибка при сохранении", 500);
        }
    }

    const displayData = [...bufferData, ...seriesData];

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = "У вас есть несохраненные изменения. Вы уверены, что хотите уйти?";
                return event.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const renderTableRow = (data, index) => {
        const isBuffer = !!data.isBuffer;
        const currentFactStock = getFieldValue(data, 'fact_stock', isBuffer, index);
        const currentKxUcenke = getFieldValue(data, 'kx_ucenke', isBuffer, index);
        const currentKxSpisaniyu = getFieldValue(data, 'kx_spisaniyu', isBuffer, index);
        const currentSeriesDate = getFieldValue(data, 'series_date', isBuffer, index);

        return (
            <tr key={data.guid || data.id || index} className="expiration_date-body">
                <td>
                    <input
                        type="date"
                        className={`input-field ${!isBuffer ? "input_disable" : ""}`}
                        value={formatDateToYYYYMMDD(currentSeriesDate || "")}
                        onChange={(e) => {
                            const key = isBuffer ? `buffer_${data.id}` : `series_${data.guid}`;
                            setChanges(prev => ({
                                ...prev,
                                [key]: {
                                    ...prev[key],
                                    series_date: e.target.value,
                                    series_date_touched: true,
                                }
                            }));
                            setHasUnsavedChanges(true);
                        }}
                        disabled={!isBuffer}
                    />
                </td>
                <td>
                    <input
                        type="text"
                        className="input-field input_disable"
                        value={getFieldValue(data, 'plane_stock', isBuffer, index) || 0}
                        disabled={true}
                        readOnly
                    />
                </td>
                <td>
                    <input
                        type="text"
                        className="input-field"
                        value={currentFactStock}
                        placeholder="Введите данные"
                        onChange={(e) => handleValueChange(data, 'fact_stock', e.target.value, isBuffer, index)}
                        onFocus={handleInputFocus}
                    />
                </td>
                <td>
                    <input
                        type="text"
                        className={`input-field ${ !resultData?.is_allow_ucen ? "input_disable" : ""}`}
                        value={currentKxUcenke}
                        placeholder="Введите данные"
                        onChange={(e) => handleValueChange(data, 'kx_ucenke', e.target.value, isBuffer, index)}
                        onFocus={handleInputFocus}
                        disabled={!resultData?.is_allow_ucen}
                    />
                    {!resultData?.is_allow_ucen && (
                        <div className={"only_tsd"}>
                            *Только из ТСД
                        </div>
                    )}
                </td>
                <td>
                    <input
                        type="text"
                        className="input-field"
                        value={currentKxSpisaniyu}
                        placeholder="Введите данные"
                        onChange={(e) => handleValueChange(data, 'kx_spisaniyu', e.target.value, isBuffer, index)}
                        onFocus={handleInputFocus}
                    />
                </td>
                <td>
                    {isBuffer && (
                        <button
                            type="button"
                            className="expiration_date-button_remove"
                            onClick={() => removeBufferItem(data.id)}
                        >
                            +
                        </button>
                    )}
                </td>
            </tr>
        );
    };

    const renderMobileCard = (data, index) => {
        const isBuffer = !!data.isBuffer;
        const currentFactStock = getFieldValue(data, 'fact_stock', isBuffer, index);
        const currentKxUcenke = getFieldValue(data, 'kx_ucenke', isBuffer, index);
        const currentKxSpisaniyu = getFieldValue(data, 'kx_spisaniyu', isBuffer, index);
        const currentSeriesDate = getFieldValue(data, 'series_date', isBuffer, index);

        return (
            <div key={data.guid || data.id || index} className="mobile_hintSeries-wrapper">
                <div className="mobile_hintSeries-item">
                    <span>Срок годности: </span>
                    <input
                        type="date"
                        className={`input-field ${!isBuffer ? "input_disable" : ""}`}
                        value={formatDateToYYYYMMDD(currentSeriesDate || "")}
                        onChange={(e) => {
                            const key = isBuffer ? `buffer_${data.id}` : `series_${data.guid}`;
                            setChanges(prev => ({
                                ...prev,
                                [key]: {
                                    ...prev[key],
                                    series_date: e.target.value,
                                    series_date_touched: true,
                                }
                            }));
                            setHasUnsavedChanges(true);
                        }}
                        disabled={!isBuffer}
                    />
                </div>
                <div className="mobile_hintSeries-item">
                    <span>Остаток по учёту: </span>
                    <input
                        type="text"
                        className="input-field input_disable"
                        value={getFieldValue(data, 'plane_stock', isBuffer, index) || 0}
                        disabled={true}
                        readOnly
                    />
                </div>
                <div className="mobile_hintSeries-item">
                    <span>Остаток по факту: </span>
                    <input
                        type="text"
                        className="input-field"
                        value={currentFactStock}
                        placeholder="Введите данные"
                        onChange={(e) => handleValueChange(data, 'fact_stock', e.target.value, isBuffer, index)}
                        onFocus={handleInputFocus}
                    />
                </div>
                <div className="mobile_hintSeries-item">
                    <span>Кол-во к уценке:
                        {!resultData?.is_allow_ucen && (
                            <div className={"only_tsd"}>
                                *Только из ТСД
                            </div>
                        )}
                    </span>
                    <input
                        type="text"
                        className={`input-field ${ !resultData?.is_allow_ucen ? "input_disable" : ""}`}
                        value={currentKxUcenke}
                        placeholder="Введите данные"
                        onChange={(e) => handleValueChange(data, 'kx_ucenke', e.target.value, isBuffer, index)}
                        onFocus={handleInputFocus}
                        disabled={!resultData?.is_allow_ucen}
                    />
                </div>
                <div className="mobile_hintSeries-item">
                    <span>Кол-во к списанию: </span>
                    <input
                        type="text"
                        className="input-field"
                        value={currentKxSpisaniyu}
                        placeholder="Введите данные"
                        onChange={(e) => handleValueChange(data, 'kx_spisaniyu', e.target.value, isBuffer, index)}
                        onFocus={handleInputFocus}
                    />
                </div>
                {isBuffer && (
                    <button
                        type="button"
                        className="expiration_date-button_remove"
                        onClick={() => removeBufferItem(data.id)}
                    >
                        Удалить
                    </button>
                )}
            </div>
        );
    };

    return (
        <>
            <ButtonBack
                hasUnsavedChanges={hasUnsavedChanges}
                onConfirmExit={showExitConfirmation}
                onConfirmReload={showReloadConfirmation}
            />
            <CustomAlert
                title={alertState.title}
                message={alertState.message}
                statusCod={alertState.statusCod}
                isOpenAlert={alertState.isOpen}
                onClose={handleCloseAlert}
                onConfirm={alertState.isConfirmMode ? handleConfirmAlert : undefined}
                confirmText={alertState.confirmText}
                cancelText={alertState.cancelText}
            />

            <form className="hint__container max--width--control hintSeries_desctop" id="SeriesForn">
                <div className="hint__container--title">
                    <div className="series_wrapper">
                        <span className="hint__container--title__text">{resultData?.name}</span>
                        <div className="series_header">
                            <img className="series_img" src={`${resultData?.attachment}`} loading="lazy" alt=""/>
                            <div className="series_data">
                                {resultData?.values?.length > 0 && resultData.values.map((data, index) => (
                                    <p key={index + 1}>{data.name}: <span>{data.value}</span></p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="expiration_dates">
                    <table className="expiration_table">
                        <thead>
                        <tr className="expiration_date-header">
                            <th>Срок годности</th>
                            <th>Остаток по учёту</th>
                            <th>Остаток по факту</th>
                            <th>Кол-во к уценке</th>
                            <th>Кол-во к списанию</th>
                            <th>Действия</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Array.isArray(displayData) && displayData.map((data, index) => renderTableRow(data, index))}
                        </tbody>
                    </table>
                    {resultData?.is_closed === false && (
                        <div className="expiration_dates-buttonWrapper">
                            <ButtonHint
                                className={`${role !== "Руководитель" && role !== "Учебный центр" ? "expiration_dates-button_add" : "disable__cancellation"}`}
                                text="Добавить еще"
                                funck={addMore}
                            />
                            <ButtonHint
                                className={`${role !== "Руководитель" && role !== "Учебный центр" ? "expiration_date-button_accept" : "disable__cancellation"}`}
                                text="Применить"
                                funck={accept}
                            />
                        </div>
                    )}
                </div>

            </form>

            <form className="hint__container max--width--control hintSeries_mobile">
                <div className="hint__container--title">
                    <div className="series_wrapper">
                        <span className="hint__container--title__text">{resultData?.name}</span>
                        <div className="series_header">
                            <img className="series_img" src={`${resultData?.attachment}`} loading="lazy" alt=""/>
                            <div className="series_data">
                                {resultData?.values?.length > 0 && resultData.values.map((data, index) => (
                                    <p key={index + 1}>{data.name}: <span>{data.value}</span></p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {resultData?.is_closed === false && (
                    <div className="expiration_dates-buttonWrapper">
                        <ButtonHint
                            className={`${role !== "Руководитель" && role !== "Учебный центр" ? "expiration_dates-button_add" : "disable__cancellation"}`}
                            text="Добавить еще"
                            funck={addMore}
                        />
                        <ButtonHint
                            className={`${role !== "Руководитель" && role !== "Учебный центр" ? "expiration_date-button_accept" : "disable__cancellation"}`}
                            text="Применить"
                            funck={accept}
                        />
                    </div>
                )}

                <div className="mobile_hintSeries">
                    {Array.isArray(displayData) && displayData.map((data, index) => renderMobileCard(data, index))}
                </div>
            </form>
        </>
    );
};

export default HintSeries;